import { VStack } from "@/components/layout/VStack";
import { Button } from "@/components/ui/Button/Button";
import {
  Dialog,
  DialogClose,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/Dialog/Dialog";
import { Label } from "@/components/ui/Label/Label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/Select/Select";
import {
  getRecencyCutoff,
  isRecentSession,
} from "@/utils/rotations/recency";
import { format } from "date-fns";
import { useMemo, useState } from "react";
import { useRotations } from "../../hooks/rotations/useRotations";
import { Link } from "react-router-dom";

const AssignPreferencesDialog = ({
  open,
  onOpenChange,
  rotation,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  rotation: any;
}) => {
  const [selected, setSelected] = useState<string | undefined>(undefined);

  const { assignPreferences, loading } = useRotations();

  const onSuccess = () => {
    onOpenChange(false);
    setSelected(undefined);
  };

  const cutoff = useMemo(() => getRecencyCutoff(), []);

  const recentSessions = useMemo(
    () =>
      (rotation.sessions ?? []).filter((s: any) =>
        isRecentSession(s, [rotation], cutoff),
      ),
    [rotation, cutoff],
  );

  const onSubmit = async () => {
    const sessions =
      selected === "all"
        ? recentSessions.map((s: any) => s._id)
        : [selected];

    await assignPreferences(sessions, onSuccess);
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>
            Assign Preferences for {format(rotation.date, "MMMM dd, yyyy")}{" "}
            Rotation
          </DialogTitle>
          <DialogDescription>
            Algorithmically assign preferences for a session of{" "}
            {format(rotation.date, "MMMM dd, yyyy")} rotation. You can manually
            assign preferences for a specific session, on the{" "}
            <Link to="/assign">assign page</Link>.
          </DialogDescription>
        </DialogHeader>
        <VStack gap="2px">
          <Label>Session</Label>
          <Select value={selected} onValueChange={setSelected}>
            <SelectTrigger>
              <SelectValue placeholder="Select a session" />
            </SelectTrigger>
            <SelectContent>
              {recentSessions.length < 1 ? (
                <SelectItem disabled value=" ">
                  Only singleton sessions found
                </SelectItem>
              ) : (
                <>
                  {recentSessions.map((s: any) => (
                    <SelectItem key={s._id} value={s._id}>
                      {s.name}
                    </SelectItem>
                  ))}
                  <SelectItem value="all">All Sessions</SelectItem>
                </>
              )}
            </SelectContent>
          </Select>
        </VStack>
        <DialogFooter>
          <DialogClose asChild>
            <Button variant="outline" disabled={loading.assignPreferences}>
              {loading.assignPreferences ? "Loading..." : "Close"}
            </Button>
          </DialogClose>
          <Button
            onClick={onSubmit}
            disabled={loading.assignPreferences || !selected}
          >
            {loading.assignPreferences ? "Loading..." : "Assign Preferences"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export { AssignPreferencesDialog };
